import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Card, Checkbox, Col, Divider, Input, InputNumber, List, Popconfirm, Row, Select, Slider, Tag, message } from 'antd';
import { Circle, GeoJSON, Polygon, Polyline } from 'react-leaflet';
import L from 'leaflet';
import pointOnFeature from '@turf/point-on-feature';
import { BaseMap } from '../../components/Map';
import axios from '../../util/Api';
import './style.css';

const { Option } = Select;

const INDIA_CENTER = [20.5937, 78.9629];
const DEFAULT_MAP_ZOOM = 5;
const FOCUS_MAP_ZOOM = 15;

const DRAW_MODE = {
  NONE: 'none',
  POLYGON: 'polygon',
  RECTANGLE: 'rectangle',
  CIRCLE: 'circle',
};

const DEFAULT_STYLE = {
  color: '#6f6f6f',
  fillColor: '#6f6f6f',
  fillOpacity: 0.2,
  weight: 1,
};
const POINT_OF_INTEREST_RESOURCE = 'PointOfInterest';

function flattenRoleAccess(roles = []) {
  return (roles || []).reduce((acc, role) => {
    return acc.concat((role && role.access) || []);
  }, []);
}

function getResourcePermissions(roles = [], resourceName = '') {
  const accessEntries = flattenRoleAccess(roles);
  const permissionSet = new Set();
  accessEntries.forEach((entry) => {
    if (!entry || entry.name !== resourceName || !Array.isArray(entry.permissions)) return;
    entry.permissions.forEach((permission) => {
      permissionSet.add(String(permission));
    });
  });
  return Array.from(permissionSet);
}

function getUserOrganisationMeta(user) {
  const organisation = user && user.organisation;
  if (!organisation) return { id: null, name: '' };
  if (typeof organisation === 'string') return { id: String(organisation), name: '' };
  const id = organisation._id || organisation.id || null;
  const name = organisation.name || organisation.label || '';
  return {
    id: id ? String(id) : null,
    name: name ? String(name) : '',
  };
}

function toGeoJsonPoint(latLng) {
  return [Number(latLng[1]), Number(latLng[0])];
}

function closeRing(ring) {
  if (!ring || !ring.length) return ring;
  const first = ring[0];
  const last = ring[ring.length - 1];
  if (first[0] === last[0] && first[1] === last[1]) {
    return ring;
  }
  return ring.concat([first]);
}

function polygonFromLatLngs(latLngs) {
  if (!latLngs || latLngs.length < 3) return null;
  const ring = closeRing(latLngs.map(toGeoJsonPoint));
  return {
    type: 'Feature',
    geometry: {
      type: 'Polygon',
      coordinates: [ring],
    },
    properties: {},
  };
}

function rectangleLatLngs(start, end) {
  if (!start || !end) return [];
  const minLat = Math.min(start[0], end[0]);
  const maxLat = Math.max(start[0], end[0]);
  const minLng = Math.min(start[1], end[1]);
  const maxLng = Math.max(start[1], end[1]);
  return [
    [minLat, minLng],
    [minLat, maxLng],
    [maxLat, maxLng],
    [maxLat, minLng],
  ];
}

function destinationPoint(center, bearingDegrees, distanceMeters) {
  const earthRadius = 6378137;
  const bearing = (bearingDegrees * Math.PI) / 180;
  const lat1 = (center[0] * Math.PI) / 180;
  const lng1 = (center[1] * Math.PI) / 180;
  const angularDistance = distanceMeters / earthRadius;

  const lat2 = Math.asin(
    Math.sin(lat1) * Math.cos(angularDistance) +
    Math.cos(lat1) * Math.sin(angularDistance) * Math.cos(bearing)
  );
  const lng2 = lng1 + Math.atan2(
    Math.sin(bearing) * Math.sin(angularDistance) * Math.cos(lat1),
    Math.cos(angularDistance) - Math.sin(lat1) * Math.sin(lat2)
  );

  return [lat2 * 180 / Math.PI, lng2 * 180 / Math.PI];
}

function circlePolygonFeature(center, radiusMeters, vertices) {
  if (!center || !radiusMeters || radiusMeters <= 0) return null;
  const points = [];
  const total = vertices || 64;
  for (let i = 0; i < total; i += 1) {
    points.push(destinationPoint(center, (360 * i) / total, radiusMeters));
  }
  return polygonFromLatLngs(points);
}

function getFeatureStyle(feature, fallbackStyle) {
  const featureStyle = feature && feature.properties && feature.properties.style;
  return {
    ...DEFAULT_STYLE,
    ...(fallbackStyle || {}),
    ...(featureStyle || {}),
  };
}

function getUserFromLocalStorage() {
  try {
    const user = window.localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  } catch (e) {
    return null;
  }
}

function getFeatureCenter(feature) {
  try {
    if (!feature || feature.type !== 'Feature' || !feature.geometry) {
      return null;
    }
    const centeredFeature = pointOnFeature(feature);
    const coordinates = centeredFeature && centeredFeature.geometry && centeredFeature.geometry.coordinates;
    if (Array.isArray(coordinates) && coordinates.length >= 2) {
      return [Number(coordinates[1]), Number(coordinates[0])];
    }
  } catch (e) {
    // Fallback below for malformed features.
  }

  try {
    const geometry = feature && feature.geometry;
    if (!geometry || geometry.type !== 'Polygon' || !geometry.coordinates || !geometry.coordinates.length) {
      return null;
    }
    const ring = geometry.coordinates[0];
    if (!ring || !ring.length) return null;
    let sumLat = 0;
    let sumLng = 0;
    let count = 0;
    ring.forEach((coordinate) => {
      if (Array.isArray(coordinate) && coordinate.length >= 2) {
        sumLng += Number(coordinate[0]);
        sumLat += Number(coordinate[1]);
        count += 1;
      }
    });
    if (!count) return null;
    return [sumLat / count, sumLng / count];
  } catch (e) {
    return null;
  }
}

function parseLatLngInput(text) {
  const value = String(text || '').trim();
  if (!value) return null;
  const parts = value.split(/[,\s]+/).filter(Boolean);
  if (parts.length !== 2) return null;

  const lat = Number(parts[0]);
  const lng = Number(parts[1]);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
  return [lat, lng];
}

function extractErrorMessage(error) {
  const msg = error && error.response && error.response.data && error.response.data.message;
  if (Array.isArray(msg)) return msg[0];
  if (typeof msg === 'string' && msg) return msg;
  return null;
}

function getShapeTypeFromFeature(feature, fallback = DRAW_MODE.POLYGON) {
  const geometryType = feature && feature.geometry && feature.geometry.type;
  if (geometryType === 'Point' || geometryType === 'MultiPoint') return 'point';
  if (geometryType === 'LineString' || geometryType === 'MultiLineString') return 'line';
  if (geometryType === 'Polygon' || geometryType === 'MultiPolygon') return fallback || DRAW_MODE.POLYGON;
  return fallback || DRAW_MODE.POLYGON;
}

function createLocalId(prefix) {
  return `${prefix || 'shape'}-${Date.now()}-${Math.floor(Math.random() * 1000000)}`;
}

function stopLeafletEventPropagation(event) {
  if (event && event.originalEvent && typeof event.originalEvent.stopPropagation === 'function') {
    event.originalEvent.stopPropagation();
  }
}

function getInteractiveLayerStyle(feature, fallbackStyle, options = {}) {
  const base = getFeatureStyle(feature, fallbackStyle);
  const selected = !!options.selected;
  const pending = !!options.pending;
  return {
    ...base,
    dashArray: pending ? '6 4' : undefined,
    weight: selected ? Math.max(Number(base.weight) || DEFAULT_STYLE.weight, 2) + 1 : base.weight,
    fillOpacity: selected ? Math.min((Number(base.fillOpacity) || DEFAULT_STYLE.fillOpacity) + 0.12, 0.85) : base.fillOpacity,
  };
}

function buildFenceFeatureCollection(fences = [], idField = '_id') {
  const features = (fences || [])
    .map((fence) => {
      const feature = fence && fence.feature;
      const fenceId = fence && fence[idField];
      if (!feature || feature.type !== 'Feature' || !fenceId) return null;
      return {
        ...feature,
        properties: {
          ...(feature.properties || {}),
          _fenceId: String(fenceId),
        },
      };
    })
    .filter(Boolean);

  if (!features.length) return null;
  return {
    type: 'FeatureCollection',
    features,
  };
}

function isCoordinatePair(value) {
  return Array.isArray(value)
    && value.length >= 2
    && Number.isFinite(Number(value[0]))
    && Number.isFinite(Number(value[1]));
}

function toLatLng(value) {
  if (!isCoordinatePair(value)) return null;
  return [Number(value[1]), Number(value[0])];
}

function areLatLngEqual(a, b) {
  return !!a
    && !!b
    && Number(a[0]) === Number(b[0])
    && Number(a[1]) === Number(b[1]);
}

function normalizeLatLngRing(latLngs) {
  const normalized = Array.isArray(latLngs)
    ? latLngs
      .filter(isCoordinatePair)
      .map((item) => [Number(item[0]), Number(item[1])])
    : [];
  if (normalized.length > 1 && areLatLngEqual(normalized[0], normalized[normalized.length - 1])) {
    return normalized.slice(0, normalized.length - 1);
  }
  return normalized;
}

function getPrimaryPolygonRingLatLngs(feature) {
  const geometry = feature && feature.geometry;
  if (!geometry || !geometry.type) return [];
  if (geometry.type === 'Polygon') {
    const ring = Array.isArray(geometry.coordinates) && geometry.coordinates.length
      ? geometry.coordinates[0]
      : [];
    return normalizeLatLngRing((ring || []).map(toLatLng).filter(Boolean));
  }
  if (geometry.type === 'MultiPolygon') {
    const firstPolygon = Array.isArray(geometry.coordinates) && geometry.coordinates.length
      ? geometry.coordinates[0]
      : [];
    const ring = Array.isArray(firstPolygon) && firstPolygon.length ? firstPolygon[0] : [];
    return normalizeLatLngRing((ring || []).map(toLatLng).filter(Boolean));
  }
  return [];
}

function calculateDistanceMeters(from, to) {
  if (!from || !to) return 0;
  return L.latLng(Number(from[0]), Number(from[1])).distanceTo(L.latLng(Number(to[0]), Number(to[1])));
}

function calculateSegmentDistancesMeters(latLngs, closePath = true) {
  const points = normalizeLatLngRing(latLngs);
  if (points.length < 2) return [];

  const distances = [];
  for (let index = 0; index < points.length - 1; index += 1) {
    distances.push(calculateDistanceMeters(points[index], points[index + 1]));
  }
  if (closePath && points.length > 2) {
    distances.push(calculateDistanceMeters(points[points.length - 1], points[0]));
  }
  return distances.filter((value) => Number.isFinite(value) && value >= 0);
}

function calculateGeodesicAreaSqm(latLngs) {
  const points = normalizeLatLngRing(latLngs);
  if (points.length < 3) return 0;

  const d2r = Math.PI / 180;
  const earthRadius = 6378137;
  let area = 0;
  for (let index = 0; index < points.length; index += 1) {
    const current = points[index];
    const next = points[(index + 1) % points.length];
    area += ((next[1] - current[1]) * d2r) * (2 + Math.sin(current[0] * d2r) + Math.sin(next[0] * d2r));
  }
  return Math.abs((area * earthRadius * earthRadius) / 2);
}

function sumNumbers(values) {
  return (values || []).reduce((sum, value) => sum + Number(value || 0), 0);
}

function formatMetricNumber(value, maxFractionDigits = 2) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return '-';
  return numericValue.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxFractionDigits,
  });
}

function formatDistanceMeters(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return '-';
  const maxFractionDigits = numericValue >= 100 ? 0 : 1;
  return `${formatMetricNumber(numericValue, maxFractionDigits)} m`;
}

function formatAreaSquareMeters(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return '-';
  const maxFractionDigits = numericValue >= 1000 ? 0 : 2;
  return `${formatMetricNumber(numericValue, maxFractionDigits)} sq m`;
}

function resolveShapeType(feature, shapeType) {
  if (typeof shapeType === 'string' && shapeType.trim()) {
    return shapeType.trim().toLowerCase();
  }
  return getShapeTypeFromFeature(feature, DRAW_MODE.POLYGON);
}

function computeShapeMetrics(feature, shapeType, options = {}) {
  if (!feature || !feature.geometry) return null;

  const resolvedShapeType = resolveShapeType(feature, shapeType);
  const ringLatLngs = getPrimaryPolygonRingLatLngs(feature);

  if (resolvedShapeType === DRAW_MODE.CIRCLE) {
    const areaSqm = ringLatLngs.length >= 3 ? calculateGeodesicAreaSqm(ringLatLngs) : null;
    const radiusOverride = Number(options.radiusOverrideMeters);
    const radiusMeters = Number.isFinite(radiusOverride) && radiusOverride > 0
      ? radiusOverride
      : (Number.isFinite(Number(areaSqm)) && Number(areaSqm) > 0 ? Math.sqrt(Number(areaSqm) / Math.PI) : null);
    return {
      areaSqm,
      radiusMeters,
      perimeterMeters: Number.isFinite(radiusMeters) ? 2 * Math.PI * radiusMeters : null,
      segmentDistancesMeters: [],
    };
  }

  if (resolvedShapeType === DRAW_MODE.POLYGON || resolvedShapeType === DRAW_MODE.RECTANGLE) {
    if (!ringLatLngs.length) return null;
    const segmentDistancesMeters = calculateSegmentDistancesMeters(ringLatLngs, true);
    return {
      areaSqm: ringLatLngs.length >= 3 ? calculateGeodesicAreaSqm(ringLatLngs) : null,
      radiusMeters: null,
      perimeterMeters: segmentDistancesMeters.length ? sumNumbers(segmentDistancesMeters) : null,
      segmentDistancesMeters,
    };
  }

  return null;
}

function getMetricPrimarySummary(metrics, shapeType) {
  if (!metrics) return '';
  const parts = [];
  if (Number.isFinite(Number(metrics.areaSqm))) {
    parts.push(`Area: ${formatAreaSquareMeters(metrics.areaSqm)}`);
  }
  if (shapeType === DRAW_MODE.CIRCLE && Number.isFinite(Number(metrics.radiusMeters))) {
    parts.push(`Radius: ${formatDistanceMeters(metrics.radiusMeters)}`);
  }
  if ((shapeType === DRAW_MODE.POLYGON || shapeType === DRAW_MODE.RECTANGLE) && Number.isFinite(Number(metrics.perimeterMeters))) {
    parts.push(`Perimeter: ${formatDistanceMeters(metrics.perimeterMeters)}`);
  }
  return parts.join(' | ');
}

function getMetricSegmentSummary(metrics, shapeType, maxItems = 4) {
  if (!metrics || !Array.isArray(metrics.segmentDistancesMeters) || !metrics.segmentDistancesMeters.length) {
    return '';
  }
  if (!(shapeType === DRAW_MODE.POLYGON || shapeType === DRAW_MODE.RECTANGLE)) {
    return '';
  }

  const prefix = shapeType === DRAW_MODE.RECTANGLE ? 'Sides' : 'Edges';
  const visible = metrics.segmentDistancesMeters.slice(0, maxItems);
  const suffixCount = Math.max(metrics.segmentDistancesMeters.length - visible.length, 0);
  const segmentText = visible
    .map((distance, index) => `${index + 1}:${formatDistanceMeters(distance)}`)
    .join(', ');
  return suffixCount
    ? `${prefix}: ${segmentText} (+${suffixCount} more)`
    : `${prefix}: ${segmentText}`;
}

function getMetricDetailLines(metrics, shapeType, options = {}) {
  if (!metrics) return [];
  const lines = [];

  if (Number.isFinite(Number(metrics.areaSqm))) {
    lines.push(`Area: ${formatAreaSquareMeters(metrics.areaSqm)}`);
  }

  if (shapeType === DRAW_MODE.CIRCLE && Number.isFinite(Number(metrics.radiusMeters))) {
    lines.push(`Radius: ${formatDistanceMeters(metrics.radiusMeters)}`);
  }

  if ((shapeType === DRAW_MODE.POLYGON || shapeType === DRAW_MODE.RECTANGLE) && Array.isArray(metrics.segmentDistancesMeters)) {
    const edgePrefix = shapeType === DRAW_MODE.RECTANGLE ? 'Side' : 'Edge';
    metrics.segmentDistancesMeters.forEach((distance, index) => {
      lines.push(`${edgePrefix} ${index + 1}: ${formatDistanceMeters(distance)}`);
    });
  }

  if (Number.isFinite(Number(metrics.perimeterMeters)) && shapeType !== DRAW_MODE.CIRCLE) {
    lines.push(`Perimeter: ${formatDistanceMeters(metrics.perimeterMeters)}`);
  }

  if (options.includeCircumference && shapeType === DRAW_MODE.CIRCLE && Number.isFinite(Number(metrics.perimeterMeters))) {
    lines.push(`Circumference: ${formatDistanceMeters(metrics.perimeterMeters)}`);
  }

  return lines;
}

function escapeHtml(text) {
  return String(text || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function buildShapeTooltipHtml(label, metrics, shapeType) {
  const safeLabel = escapeHtml(String(label || ''));
  const detailLines = getMetricDetailLines(metrics, shapeType, { includeCircumference: true });
  const metricHtml = detailLines
    .map((line) => `<div class="geo-fence-tooltip-row">${escapeHtml(line)}</div>`)
    .join('');
  return [
    '<div class="geo-fence-tooltip-content">',
    safeLabel ? `<div class="geo-fence-tooltip-title">${safeLabel}</div>` : '',
    metricHtml,
    '</div>',
  ].join('');
}

function getMidPointLatLng(start, end) {
  if (!start || !end) return null;
  return [
    (Number(start[0]) + Number(end[0])) / 2,
    (Number(start[1]) + Number(end[1])) / 2,
  ];
}

function toGeoJsonCoordinates(latLng) {
  if (!isCoordinatePair(latLng)) return null;
  return [Number(latLng[1]), Number(latLng[0])];
}

function createMeasurementLabelFeature(latLng, text, id, kind = 'edge') {
  const coordinates = toGeoJsonCoordinates(latLng);
  if (!coordinates || !text) return null;
  return {
    type: 'Feature',
    id,
    geometry: {
      type: 'Point',
      coordinates,
    },
    properties: {
      text: String(text),
      kind: String(kind || 'edge'),
    },
  };
}

function buildDistanceLabelFeatures(latLngs, distances, options = {}) {
  const points = normalizeLatLngRing(latLngs);
  if (!points.length || !Array.isArray(distances) || !distances.length) return [];

  const closePath = !!options.closePath;
  const kind = options.kind || 'edge';
  const idPrefix = options.idPrefix || 'measure';
  const segmentCount = closePath ? points.length : (points.length - 1);
  const features = [];

  for (let index = 0; index < segmentCount; index += 1) {
    const nextIndex = closePath ? ((index + 1) % points.length) : (index + 1);
    if (!closePath && nextIndex >= points.length) break;
    const distance = Number(distances[index]);
    if (!Number.isFinite(distance)) continue;
    const midpoint = getMidPointLatLng(points[index], points[nextIndex]);
    const feature = createMeasurementLabelFeature(midpoint, formatDistanceMeters(distance), `${idPrefix}-${kind}-${index}`, kind);
    if (feature) {
      features.push(feature);
    }
  }

  return features;
}

function buildMeasurementLabelIcon(text, kind = 'edge') {
  const safeText = escapeHtml(String(text || ''));
  const normalizedKind = String(kind || 'edge');
  return L.divIcon({
    className: `geo-fence-measurement-label-layer geo-fence-measurement-label-${normalizedKind}`,
    html: `<span class="geo-fence-measurement-label-text">${safeText}</span>`,
    iconSize: [0, 0],
    iconAnchor: [0, 0],
  });
}

export default function GeoFence({ authUser }) {
  const [organisations, setOrganisations] = useState([]);
  const [selectedOrganisations, setSelectedOrganisations] = useState([]);
  const [geoFences, setGeoFences] = useState([]);
  const [loadingFences, setLoadingFences] = useState(false);
  const [savingFence, setSavingFence] = useState(false);
  const [mapZoom, setMapZoom] = useState(DEFAULT_MAP_ZOOM);
  const [mapCenter, setMapCenter] = useState(INDIA_CENTER);
  const [mapSearchQuery, setMapSearchQuery] = useState('');
  const [searchingLocation, setSearchingLocation] = useState(false);

  const [drawMode, setDrawMode] = useState(DRAW_MODE.NONE);
  const [workingPoints, setWorkingPoints] = useState([]);
  const [previewPoint, setPreviewPoint] = useState(null);
  const [rectangleStart, setRectangleStart] = useState(null);
  const [rectangleCurrent, setRectangleCurrent] = useState(null);
  const [circleCenter, setCircleCenter] = useState(null);
  const [circleRadiusMeters, setCircleRadiusMeters] = useState(0);

  const [draftFeature, setDraftFeature] = useState(null);
  const [draftShapeType, setDraftShapeType] = useState(DRAW_MODE.POLYGON);
  const [pendingFences, setPendingFences] = useState([]);
  const [savingAllPending, setSavingAllPending] = useState(false);
  const [editingExistingFence, setEditingExistingFence] = useState(null);
  const [selectedExistingFenceId, setSelectedExistingFenceId] = useState(null);
  const [selectedExistingFenceIds, setSelectedExistingFenceIds] = useState([]);
  const [selectedPendingFenceId, setSelectedPendingFenceId] = useState(null);
  const [savedFenceSearchQuery, setSavedFenceSearchQuery] = useState('');

  const [shapeName, setShapeName] = useState('');
  const [shapeColor, setShapeColor] = useState('#6f6f6f');
  const [fillOpacity, setFillOpacity] = useState(0.2);
  const [strokeWeight, setStrokeWeight] = useState(1);

  const resolvedUser = authUser || getUserFromLocalStorage();
  const isSuperAdmin = !!(resolvedUser && resolvedUser.superAdmin);
  const loggedUserOrganisation = useMemo(
    () => getUserOrganisationMeta(resolvedUser),
    [resolvedUser],
  );
  const pointOfInterestPermissions = useMemo(() => {
    if (isSuperAdmin) return ['c', 'r', 'u', 'd'];
    return getResourcePermissions((resolvedUser && resolvedUser.roles) || [], POINT_OF_INTEREST_RESOURCE);
  }, [isSuperAdmin, resolvedUser]);
  const hasPointOfInterestAccess = isSuperAdmin || pointOfInterestPermissions.length > 0;
  const canReadPointOfInterest = isSuperAdmin || pointOfInterestPermissions.includes('r');
  const canCreatePointOfInterest = isSuperAdmin || pointOfInterestPermissions.includes('c');
  const canUpdatePointOfInterest = isSuperAdmin || pointOfInterestPermissions.includes('u');
  const canDeletePointOfInterest = isSuperAdmin || pointOfInterestPermissions.includes('d');
  const canEditExistingPointOfInterest = canUpdatePointOfInterest || canDeletePointOfInterest;
  const canDrawPointOfInterest = canCreatePointOfInterest || canUpdatePointOfInterest;
  const canViewExistingPointOfInterest = canReadPointOfInterest || canEditExistingPointOfInterest;
  const mapRef = useRef();
  const organisationNameMap = useMemo(() => {
    const map = {};
    organisations.forEach((org) => {
      map[String(org._id)] = org.name;
    });
    if (loggedUserOrganisation.id && loggedUserOrganisation.name) {
      map[String(loggedUserOrganisation.id)] = loggedUserOrganisation.name;
    }
    return map;
  }, [organisations, loggedUserOrganisation]);
  const selectedPendingFence = useMemo(
    () => pendingFences.find((item) => item && item.localId === selectedPendingFenceId) || null,
    [pendingFences, selectedPendingFenceId],
  );
  const filteredSavedGeoFences = useMemo(() => {
    const query = String(savedFenceSearchQuery || '').trim().toLowerCase();
    if (!query) return geoFences;
    return (geoFences || []).filter((item) => {
      const name = String((item && item.name) || '').toLowerCase();
      return name.includes(query);
    });
  }, [geoFences, savedFenceSearchQuery]);
  const savedFenceById = useMemo(() => {
    const map = {};
    (geoFences || []).forEach((item) => {
      if (!item || !item._id) return;
      map[String(item._id)] = item;
    });
    return map;
  }, [geoFences]);
  const pendingFenceById = useMemo(() => {
    const map = {};
    (pendingFences || []).forEach((item) => {
      if (!item || !item.localId) return;
      map[String(item.localId)] = item;
    });
    return map;
  }, [pendingFences]);
  const savedFenceFeatureCollection = useMemo(
    () => buildFenceFeatureCollection(geoFences, '_id'),
    [geoFences],
  );
  const pendingFenceFeatureCollection = useMemo(
    () => buildFenceFeatureCollection(pendingFences, 'localId'),
    [pendingFences],
  );
  const shouldBindSavedFenceTooltips = useMemo(
    () => (geoFences || []).length <= 200 || mapZoom >= 14,
    [geoFences, mapZoom],
  );

  const resetDrawingState = () => {
    setWorkingPoints([]);
    setPreviewPoint(null);
    setRectangleStart(null);
    setRectangleCurrent(null);
    setCircleCenter(null);
    setCircleRadiusMeters(0);
  };

  const loadShapeIntoEditor = (shape, options = {}) => {
    if (!shape || !shape.feature) return;

    const feature = shape.feature;
    const featureStyle = feature && feature.properties && feature.properties.style;
    const resolvedColor = (shape && shape.color)
      || (featureStyle && (featureStyle.color || featureStyle.fillColor))
      || DEFAULT_STYLE.color;
    const resolvedFillOpacity = typeof (shape && shape.fillOpacity) === 'number'
      ? shape.fillOpacity
      : (typeof (featureStyle && featureStyle.fillOpacity) === 'number' ? featureStyle.fillOpacity : DEFAULT_STYLE.fillOpacity);
    const resolvedWeight = typeof (shape && shape.weight) === 'number'
      ? shape.weight
      : (typeof (featureStyle && featureStyle.weight) === 'number' ? featureStyle.weight : DEFAULT_STYLE.weight);

    setDraftFeature(feature);
    setDraftShapeType(resolveShapeType(feature, shape && shape.shapeType));
    setShapeName(
      shape.name
      || (feature && feature.properties && feature.properties.name)
      || '',
    );
    setShapeColor(resolvedColor);
    setFillOpacity(resolvedFillOpacity);
    setStrokeWeight(resolvedWeight);
    setDrawMode(DRAW_MODE.NONE);
    resetDrawingState();

    if (options.existing) {
      setEditingExistingFence(shape);
      setSelectedExistingFenceId(shape._id || null);
      setSelectedPendingFenceId(null);
    } else {
      setEditingExistingFence(null);
      setSelectedExistingFenceId(null);
    }

    const center = getFeatureCenter(feature);
    if (center) {
      flyToLocation(center, FOCUS_MAP_ZOOM);
    }
  };

  const flyToLocation = (location, zoomLevel = FOCUS_MAP_ZOOM) => {
    if (!location || location.length !== 2) return;
    const normalizedCenter = [Number(location[0]), Number(location[1])];
    setMapCenter(normalizedCenter);
    setMapZoom(zoomLevel);
    if (mapRef.current && mapRef.current.leafletElement) {
      mapRef.current.leafletElement.flyTo(normalizedCenter, zoomLevel, { animate: true, duration: 0.8 });
    }
  };

  const fetchOrganisations = async () => {
    try {
      const response = await axios.get('/organisation/distinct');
      const orgs = Array.isArray(response.data) ? response.data : [];
      setOrganisations(orgs);
      if ((!selectedOrganisations || !selectedOrganisations.length) && orgs.length) {
        setSelectedOrganisations([String(orgs[0]._id)]);
      }
    } catch (error) {
      message.error('Unable to load organisations');
    }
  };

  const fetchGeoFences = async (organisationIds) => {
    const orgIds = (organisationIds || []).filter(Boolean);
    if (!orgIds.length) {
      setGeoFences([]);
      return;
    }
    try {
      setLoadingFences(true);
      const responses = await Promise.all(orgIds.map((organisationId) => (
        axios.get(`/geo-fence/superadmin?organisation=${encodeURIComponent(organisationId)}&limit=500&offset=0&sortField=updatedAt&sortOrder=-1`)
      )));

      const merged = [];
      responses.forEach((response, index) => {
        const organisationId = orgIds[index];
        const data = response && response.data && response.data.data;
        if (Array.isArray(data)) {
          data.forEach((item) => {
            merged.push({
              ...item,
              _sourceOrganisation: organisationId,
              _sourceOrganisationName: organisationNameMap[organisationId] || '',
            });
          });
        }
      });

      merged.sort((a, b) => {
        const ta = Number(new Date(a && a.updatedAt)) || 0;
        const tb = Number(new Date(b && b.updatedAt)) || 0;
        return tb - ta;
      });

      setGeoFences(merged);
      const fenceWithCenter = merged.find((item) => getFeatureCenter(item && item.feature));
      if (fenceWithCenter) {
        const center = getFeatureCenter(fenceWithCenter.feature);
        if (center) {
          flyToLocation(center, FOCUS_MAP_ZOOM);
        }
      }
    } catch (error) {
      message.error('Unable to load points of interest');
      setGeoFences([]);
    } finally {
      setLoadingFences(false);
    }
  };

  useEffect(() => {
    if (!hasPointOfInterestAccess) return;
    if (!isSuperAdmin) return;
    fetchOrganisations();
  }, [isSuperAdmin, hasPointOfInterestAccess]);

  useEffect(() => {
    if (!hasPointOfInterestAccess) return;
    if (isSuperAdmin) return;

    const userOrganisationId = loggedUserOrganisation && loggedUserOrganisation.id;
    if (!userOrganisationId) {
      setOrganisations([]);
      setSelectedOrganisations([]);
      return;
    }

    const userOrganisationName = (loggedUserOrganisation && loggedUserOrganisation.name) || 'Current Organisation';
    setOrganisations((prev) => {
      if (prev && prev.length === 1 && String(prev[0]._id) === String(userOrganisationId)) {
        return prev;
      }
      return [{ _id: String(userOrganisationId), name: userOrganisationName }];
    });
    setSelectedOrganisations((prev) => {
      if (prev && prev.length === 1 && String(prev[0]) === String(userOrganisationId)) {
        return prev;
      }
      return [String(userOrganisationId)];
    });
  }, [isSuperAdmin, hasPointOfInterestAccess, loggedUserOrganisation]);

  useEffect(() => {
    if (!hasPointOfInterestAccess) return;
    if (!canReadPointOfInterest) {
      setGeoFences([]);
      return;
    }
    fetchGeoFences(selectedOrganisations);
  }, [selectedOrganisations, hasPointOfInterestAccess, canReadPointOfInterest, organisationNameMap]);

  useEffect(() => {
    const existingIds = new Set((geoFences || []).map((item) => String(item && item._id)));
    setSelectedExistingFenceIds((prev) => (prev || []).filter((id) => existingIds.has(String(id))));
  }, [geoFences]);

  const handleDelete = async (id) => {
    if (!canDeletePointOfInterest) {
      message.warning('You do not have permission to delete points of interest');
      return;
    }
    try {
      await axios.delete(`/geo-fence/${id}`);
      message.success('Point of interest deleted');
      setSelectedExistingFenceIds((prev) => (prev || []).filter((item) => String(item) !== String(id)));
      if (selectedExistingFenceId === id) {
        clearCurrentDraftOnly();
      }
      if (canReadPointOfInterest) {
        fetchGeoFences(selectedOrganisations);
      }
    } catch (error) {
      message.error('Unable to delete point of interest');
    }
  };

  const toggleExistingFenceSelection = (id, checked) => {
    const normalizedId = String(id);
    setSelectedExistingFenceIds((prev) => {
      const current = Array.isArray(prev) ? prev.map((item) => String(item)) : [];
      if (checked) {
        if (current.includes(normalizedId)) return current;
        return current.concat([normalizedId]);
      }
      return current.filter((item) => item !== normalizedId);
    });
  };

  const toggleSelectAllFilteredSavedFences = (checked) => {
    const filteredIds = (filteredSavedGeoFences || [])
      .map((item) => String(item && item._id))
      .filter(Boolean);
    if (checked) {
      setSelectedExistingFenceIds((prev) => Array.from(new Set([...(prev || []).map(String), ...filteredIds])));
      return;
    }
    setSelectedExistingFenceIds((prev) => (prev || []).map(String).filter((id) => !filteredIds.includes(id)));
  };

  const deleteSelectedSavedFences = async () => {
    if (!canDeletePointOfInterest) {
      message.warning('You do not have permission to delete points of interest');
      return;
    }
    const ids = Array.from(new Set((selectedExistingFenceIds || []).map((id) => String(id)).filter(Boolean)));
    if (!ids.length) {
      message.warning('Select saved points of interest first');
      return;
    }
    try {
      const results = await Promise.allSettled(ids.map((id) => axios.delete(`/geo-fence/${id}`)));
      const successCount = results.filter((result) => result.status === 'fulfilled').length;
      const failedCount = results.length - successCount;
      if (successCount) {
        message.success(`Deleted ${successCount} point(s) of interest`);
      }
      if (failedCount) {
        message.warning(`Failed to delete ${failedCount} point(s) of interest`);
      }
      const deletedIds = ids.filter((_, index) => results[index].status === 'fulfilled');
      if (deletedIds.includes(String(selectedExistingFenceId))) {
        clearCurrentDraftOnly();
      }
      setSelectedExistingFenceIds((prev) => (prev || []).filter((id) => !deletedIds.includes(String(id))));
      if (canReadPointOfInterest) {
        fetchGeoFences(selectedOrganisations);
      }
    } catch (error) {
      message.error('Unable to delete selected points of interest');
    }
  };

  const setMode = (mode) => {
    if (mode !== DRAW_MODE.NONE && !canDrawPointOfInterest) {
      message.warning('You do not have permission to draw or modify points of interest');
      return;
    }

    if (editingExistingFence) {
      // Keep the selected existing record in edit context while redrawing geometry.
      if (mode === DRAW_MODE.NONE) {
        resetDrawingState();
        setDrawMode(DRAW_MODE.NONE);
        if (!draftFeature && editingExistingFence.feature) {
          setDraftFeature(editingExistingFence.feature);
          setDraftShapeType(resolveShapeType(editingExistingFence.feature, editingExistingFence.shapeType));
        }
        return;
      }

      resetDrawingState();
      setDraftFeature(null);
      setDraftShapeType(mode);
      setSelectedPendingFenceId(null);
      setDrawMode(mode);
      return;
    }

    resetDrawingState();
    setDraftFeature(null);
    setDraftShapeType(mode === DRAW_MODE.NONE ? DRAW_MODE.POLYGON : mode);
    setShapeName('');
    setEditingExistingFence(null);
    setSelectedExistingFenceId(null);
    setSelectedPendingFenceId(null);
    setDrawMode(mode);
  };

  const cancelActiveDrawing = () => {
    resetDrawingState();
    setDrawMode(DRAW_MODE.NONE);
  };

  const finishPolygon = () => {
    if (workingPoints.length < 3) {
      message.warning('Polygon needs at least 3 points');
      return;
    }
    const feature = polygonFromLatLngs(workingPoints);
    setDraftFeature(feature);
    setDraftShapeType(DRAW_MODE.POLYGON);
    setDrawMode(DRAW_MODE.NONE);
    resetDrawingState();
  };

  const finalizeRectangle = (endPoint) => {
    const corners = rectangleLatLngs(rectangleStart, endPoint);
    if (!corners.length) return;
    const feature = polygonFromLatLngs(corners);
    setDraftFeature(feature);
    setDraftShapeType(DRAW_MODE.RECTANGLE);
    setDrawMode(DRAW_MODE.NONE);
    resetDrawingState();
  };

  const finalizeCircle = (edgePoint) => {
    const radius = L.latLng(circleCenter[0], circleCenter[1]).distanceTo(L.latLng(edgePoint[0], edgePoint[1]));
    if (!radius || radius < 5) {
      message.warning('Circle radius is too small');
      return;
    }
    const feature = circlePolygonFeature(circleCenter, radius, 72);
    setDraftFeature(feature);
    setDraftShapeType(DRAW_MODE.CIRCLE);
    setDrawMode(DRAW_MODE.NONE);
    resetDrawingState();
  };

  const handleMapClick = (event) => {
    if (!canDrawPointOfInterest) return;
    if (!event || !event.latlng) return;
    const point = [event.latlng.lat, event.latlng.lng];
    if (drawMode === DRAW_MODE.POLYGON) {
      setWorkingPoints(function (prev) {
        return prev.concat([point]);
      });
      return;
    }
    if (drawMode === DRAW_MODE.RECTANGLE) {
      if (!rectangleStart) {
        setRectangleStart(point);
        setRectangleCurrent(point);
      } else {
        finalizeRectangle(point);
      }
      return;
    }
    if (drawMode === DRAW_MODE.CIRCLE) {
      if (!circleCenter) {
        setCircleCenter(point);
        setCircleRadiusMeters(0);
      } else {
        finalizeCircle(point);
      }
    }
  };

  const handleMapMouseMove = (event) => {
    if (!canDrawPointOfInterest) return;
    if (!event || !event.latlng) return;
    const point = [event.latlng.lat, event.latlng.lng];
    if (drawMode === DRAW_MODE.POLYGON && workingPoints.length) {
      setPreviewPoint(point);
      return;
    }
    if (drawMode === DRAW_MODE.RECTANGLE && rectangleStart) {
      setRectangleCurrent(point);
      return;
    }
    if (drawMode === DRAW_MODE.CIRCLE && circleCenter) {
      const radius = L.latLng(circleCenter[0], circleCenter[1]).distanceTo(L.latLng(point[0], point[1]));
      setCircleRadiusMeters(radius);
    }
  };

  const getStyledFeatureFromEditor = () => ({
    ...draftFeature,
    properties: {
      ...(draftFeature && draftFeature.properties ? draftFeature.properties : {}),
      name: String(shapeName || '').trim(),
      style: {
        color: shapeColor,
        fillColor: shapeColor,
        fillOpacity: Number(fillOpacity),
        weight: Number(strokeWeight),
      },
    },
  });

  const validateEditorShape = () => {
    const trimmedName = String(shapeName || '').trim();
    if (!trimmedName) {
      message.warning('Point of interest name is required');
      return false;
    }
    if (!draftFeature) {
      message.warning('Please draw a shape first');
      return false;
    }
    return true;
  };

  const clearCurrentDraftOnly = () => {
    setDraftFeature(null);
    setDraftShapeType(DRAW_MODE.POLYGON);
    setShapeName('');
    setEditingExistingFence(null);
    setSelectedExistingFenceId(null);
    setSelectedPendingFenceId(null);
    resetDrawingState();
    setDrawMode(DRAW_MODE.NONE);
  };

  const addCurrentShapeToBatch = () => {
    if (!canCreatePointOfInterest) {
      message.warning('You do not have permission to create points of interest');
      return;
    }
    if (editingExistingFence) {
      message.warning('You are editing an existing point of interest. Update it first or clear current edit.');
      return;
    }
    if (!validateEditorShape()) return;

    const styledFeature = styledDraftFeature || getStyledFeatureFromEditor();
    const pendingItem = {
      localId: createLocalId('pending'),
      name: String(shapeName || '').trim(),
      feature: styledFeature,
      shapeType: resolveShapeType(styledFeature, draftShapeType),
      color: shapeColor,
      fillColor: shapeColor,
      fillOpacity: Number(fillOpacity),
      weight: Number(strokeWeight),
      isActive: true,
    };

    setPendingFences((prev) => prev.concat([pendingItem]));
    clearCurrentDraftOnly();
    message.success('Shape added to pending batch');
  };

  const saveAllPendingShapes = async () => {
    if (!canCreatePointOfInterest) {
      message.warning('You do not have permission to create points of interest');
      return;
    }
    const targetOrganisations = (selectedOrganisations || []).filter(Boolean);
    if (!targetOrganisations.length) {
      message.warning('Select at least one organisation');
      return;
    }
    if (!pendingFences.length) {
      message.warning(draftFeature ? 'Add current draft to batch before saving' : 'No pending shapes to save');
      return;
    }

    try {
      setSavingAllPending(true);
      const tasks = [];
      pendingFences.forEach((shape) => {
        targetOrganisations.forEach((organisationId) => {
          tasks.push({
            localId: shape.localId,
            request: axios.post('/geo-fence', {
              name: shape.name,
              organisation: organisationId,
              feature: shape.feature,
              shapeType: shape.shapeType,
              color: shape.color,
              fillColor: shape.fillColor || shape.color,
              fillOpacity: Number(shape.fillOpacity),
              weight: Number(shape.weight),
              isActive: true,
            }),
          });
        });
      });

      const results = await Promise.allSettled(tasks.map((task) => task.request));
      const successCount = results.filter((result) => result.status === 'fulfilled').length;
      const failResults = [];
      const failedShapeIds = new Set();
      results.forEach((result, index) => {
        if (result.status === 'rejected') {
          failResults.push(result);
          failedShapeIds.add(tasks[index].localId);
        }
      });

      if (successCount > 0) {
        message.success(`Saved ${successCount} point of interest entries`);
      }
      if (!failResults.length) {
        setPendingFences([]);
      } else {
        const firstError = failResults[0] && failResults[0].reason;
        const responseMessage = extractErrorMessage(firstError);
        setPendingFences((prev) => prev.filter((item) => failedShapeIds.has(item.localId)));
        message.warning(
          `Failed for ${failResults.length} entries${responseMessage ? `: ${responseMessage}` : ''}. Only failed shapes are kept for retry.`,
        );
      }
      if (canReadPointOfInterest) {
        fetchGeoFences(targetOrganisations);
      }
    } catch (error) {
      const responseMessage = extractErrorMessage(error);
      message.error(responseMessage || 'Unable to save pending shapes');
    } finally {
      setSavingAllPending(false);
    }
  };

  const updateExistingFence = async () => {
    if (!canUpdatePointOfInterest) {
      message.warning('You do not have permission to update points of interest');
      return;
    }
    if (!editingExistingFence || !editingExistingFence._id) {
      message.warning('Select an existing point of interest to edit');
      return;
    }
    if (!validateEditorShape()) return;

    const styledFeature = styledDraftFeature || getStyledFeatureFromEditor();
    const payload = {
      name: String(shapeName || '').trim(),
      organisation: editingExistingFence._sourceOrganisation || editingExistingFence.organisation || null,
      feature: styledFeature,
      shapeType: resolveShapeType(styledFeature, draftShapeType),
      color: shapeColor,
      fillColor: shapeColor,
      fillOpacity: Number(fillOpacity),
      weight: Number(strokeWeight),
      isActive: true,
    };

    try {
      setSavingFence(true);
      await axios.put(`/geo-fence/${editingExistingFence._id}`, payload);
      message.success('Point of interest updated');
      clearCurrentDraftOnly();
      if (canReadPointOfInterest) {
        fetchGeoFences(selectedOrganisations);
      }
    } catch (error) {
      const responseMessage = extractErrorMessage(error);
      message.error(responseMessage || 'Unable to update point of interest');
    } finally {
      setSavingFence(false);
    }
  };

  const removePendingFence = (localId) => {
    if (!canCreatePointOfInterest) return;
    setPendingFences((prev) => prev.filter((item) => item.localId !== localId));
    if (selectedPendingFenceId === localId) {
      setSelectedPendingFenceId(null);
    }
  };

  const clearAllPendingFences = () => {
    if (!canCreatePointOfInterest) return;
    setPendingFences([]);
    setSelectedPendingFenceId(null);
  };

  const startEditPendingFence = (localId) => {
    if (!canCreatePointOfInterest) return;
    const item = pendingFences.find((fence) => fence && fence.localId === localId);
    if (!item) return;
    setPendingFences((prev) => prev.filter((fence) => fence.localId !== localId));
    setSelectedPendingFenceId(null);
    loadShapeIntoEditor(item, { existing: false });
  };

  const startEditExistingFence = (fence) => {
    if (!canEditExistingPointOfInterest) return;
    if (!fence) return;
    if (
      editingExistingFence
      && drawMode !== DRAW_MODE.NONE
      && String(editingExistingFence._id || '') !== String(fence._id || '')
    ) {
      message.warning('Finish or cancel the current shape redraw before editing another point of interest');
      return;
    }
    loadShapeIntoEditor(fence, { existing: true });
  };

  const handleExistingFenceLayerClick = (fence, event) => {
    stopLeafletEventPropagation(event);
    if (canEditExistingPointOfInterest) {
      startEditExistingFence(fence);
      return;
    }
    if (fence && fence._id) {
      setSelectedExistingFenceId(fence._id);
      setSelectedPendingFenceId(null);
    }
  };

  const handlePendingFenceLayerClick = (fence, event) => {
    stopLeafletEventPropagation(event);
    if (!fence || !fence.localId) return;
    setSelectedPendingFenceId(fence.localId);
    setSelectedExistingFenceId(null);
  };

  const handleMapZoomEnd = () => {
    if (mapRef.current && mapRef.current.leafletElement) {
      const leafletMap = mapRef.current.leafletElement;
      setMapZoom(leafletMap.getZoom());
      const center = leafletMap.getCenter();
      if (center) {
        setMapCenter([Number(center.lat), Number(center.lng)]);
      }
    }
  };

  const handleMapMoveEnd = () => {
    if (mapRef.current && mapRef.current.leafletElement) {
      const leafletMap = mapRef.current.leafletElement;
      const center = leafletMap.getCenter();
      if (center) {
        setMapCenter([Number(center.lat), Number(center.lng)]);
      }
    }
  };

  const handleMapSearch = async () => {
    const query = String(mapSearchQuery || '').trim();
    if (!query) {
      message.warning('Enter a place or lat,lng');
      return;
    }

    const directCoords = parseLatLngInput(query);
    if (directCoords) {
      flyToLocation(directCoords, FOCUS_MAP_ZOOM);
      return;
    }

    try {
      setSearchingLocation(true);
      const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(query)}`, {
        headers: {
          Accept: 'application/json',
        },
      });
      if (!response.ok) {
        throw new Error('Location search failed');
      }
      const data = await response.json();
      const first = Array.isArray(data) && data.length ? data[0] : null;
      if (!first || !first.lat || !first.lon) {
        message.warning('No location found for this search');
        return;
      }

      const position = [Number(first.lat), Number(first.lon)];
      flyToLocation(position, FOCUS_MAP_ZOOM);
    } catch (error) {
      message.error('Unable to search this location right now');
    } finally {
      setSearchingLocation(false);
    }
  };

  const previewRectangle = useMemo(() => rectangleLatLngs(rectangleStart, rectangleCurrent), [rectangleStart, rectangleCurrent]);
  const polygonLivePoints = useMemo(() => {
    if (drawMode !== DRAW_MODE.POLYGON) return [];
    if (previewPoint && workingPoints.length) {
      return workingPoints.concat([previewPoint]);
    }
    return workingPoints;
  }, [drawMode, previewPoint, workingPoints]);
  const styledDraftFeature = useMemo(() => {
    if (!draftFeature) return null;
    return getStyledFeatureFromEditor();
  }, [draftFeature, shapeName, shapeColor, fillOpacity, strokeWeight]);
  const draftShapeMetrics = useMemo(
    () => (styledDraftFeature ? computeShapeMetrics(styledDraftFeature, draftShapeType) : null),
    [styledDraftFeature, draftShapeType],
  );
  const liveShapeMetrics = useMemo(() => {
    if (drawMode === DRAW_MODE.POLYGON) {
      const openSegmentDistances = calculateSegmentDistancesMeters(polygonLivePoints, false);
      let areaSqm = null;
      if (polygonLivePoints.length >= 3) {
        const previewFeature = polygonFromLatLngs(polygonLivePoints);
        const previewMetrics = computeShapeMetrics(previewFeature, DRAW_MODE.POLYGON);
        areaSqm = previewMetrics ? previewMetrics.areaSqm : null;
      }
      return {
        areaSqm,
        radiusMeters: null,
        perimeterMeters: openSegmentDistances.length ? sumNumbers(openSegmentDistances) : null,
        segmentDistancesMeters: openSegmentDistances,
      };
    }

    if (drawMode === DRAW_MODE.RECTANGLE && previewRectangle.length >= 4) {
      const previewFeature = polygonFromLatLngs(previewRectangle);
      return computeShapeMetrics(previewFeature, DRAW_MODE.RECTANGLE);
    }

    if (drawMode === DRAW_MODE.CIRCLE && circleCenter && circleRadiusMeters > 0) {
      const previewFeature = circlePolygonFeature(circleCenter, circleRadiusMeters, 72);
      return computeShapeMetrics(previewFeature, DRAW_MODE.CIRCLE, { radiusOverrideMeters: circleRadiusMeters });
    }

    return null;
  }, [drawMode, polygonLivePoints, previewRectangle, circleCenter, circleRadiusMeters]);
  const activeMetricsShapeType = drawMode !== DRAW_MODE.NONE ? drawMode : draftShapeType;
  const activeMetrics = drawMode !== DRAW_MODE.NONE ? liveShapeMetrics : draftShapeMetrics;
  const activeMetricLines = useMemo(
    () => getMetricDetailLines(activeMetrics, activeMetricsShapeType, { includeCircumference: true }),
    [activeMetrics, activeMetricsShapeType],
  );
  const measurementLabelGeoJson = useMemo(() => {
    const features = [];

    if (drawMode === DRAW_MODE.POLYGON) {
      const segmentDistances = liveShapeMetrics && liveShapeMetrics.segmentDistancesMeters;
      features.push(
        ...buildDistanceLabelFeatures(polygonLivePoints, segmentDistances, {
          closePath: false,
          kind: 'edge',
          idPrefix: 'live-polygon',
        })
      );

      if (liveShapeMetrics && Number.isFinite(Number(liveShapeMetrics.areaSqm)) && polygonLivePoints.length >= 3) {
        const polygonFeature = polygonFromLatLngs(polygonLivePoints);
        const center = getFeatureCenter(polygonFeature);
        const areaFeature = createMeasurementLabelFeature(
          center,
          `Area ${formatAreaSquareMeters(liveShapeMetrics.areaSqm)}`,
          'live-polygon-area',
          'area',
        );
        if (areaFeature) {
          features.push(areaFeature);
        }
      }
    } else if (drawMode === DRAW_MODE.RECTANGLE && previewRectangle.length >= 4) {
      const segmentDistances = liveShapeMetrics && liveShapeMetrics.segmentDistancesMeters;
      features.push(
        ...buildDistanceLabelFeatures(previewRectangle, segmentDistances, {
          closePath: true,
          kind: 'side',
          idPrefix: 'live-rectangle',
        })
      );

      if (liveShapeMetrics && Number.isFinite(Number(liveShapeMetrics.areaSqm))) {
        const rectangleFeature = polygonFromLatLngs(previewRectangle);
        const center = getFeatureCenter(rectangleFeature);
        const areaFeature = createMeasurementLabelFeature(
          center,
          `Area ${formatAreaSquareMeters(liveShapeMetrics.areaSqm)}`,
          'live-rectangle-area',
          'area',
        );
        if (areaFeature) {
          features.push(areaFeature);
        }
      }
    } else if (drawMode === DRAW_MODE.CIRCLE && circleCenter && liveShapeMetrics) {
      if (Number.isFinite(Number(liveShapeMetrics.areaSqm))) {
        const areaFeature = createMeasurementLabelFeature(
          circleCenter,
          `Area ${formatAreaSquareMeters(liveShapeMetrics.areaSqm)}`,
          'live-circle-area',
          'area',
        );
        if (areaFeature) {
          features.push(areaFeature);
        }
      }
      if (Number.isFinite(Number(liveShapeMetrics.radiusMeters)) && Number(liveShapeMetrics.radiusMeters) > 0) {
        const radiusEdge = destinationPoint(circleCenter, 90, Number(liveShapeMetrics.radiusMeters));
        const radiusMidPoint = getMidPointLatLng(circleCenter, radiusEdge);
        const radiusFeature = createMeasurementLabelFeature(
          radiusMidPoint,
          `R ${formatDistanceMeters(liveShapeMetrics.radiusMeters)}`,
          'live-circle-radius',
          'radius',
        );
        if (radiusFeature) {
          features.push(radiusFeature);
        }
      }
    } else if (drawMode === DRAW_MODE.NONE && styledDraftFeature && draftShapeMetrics) {
      const shapeType = resolveShapeType(styledDraftFeature, draftShapeType);
      if (shapeType === DRAW_MODE.POLYGON || shapeType === DRAW_MODE.RECTANGLE) {
        const ringLatLngs = getPrimaryPolygonRingLatLngs(styledDraftFeature);
        features.push(
          ...buildDistanceLabelFeatures(ringLatLngs, draftShapeMetrics.segmentDistancesMeters, {
            closePath: true,
            kind: shapeType === DRAW_MODE.RECTANGLE ? 'side' : 'edge',
            idPrefix: 'draft-shape',
          })
        );
        if (Number.isFinite(Number(draftShapeMetrics.areaSqm))) {
          const center = getFeatureCenter(styledDraftFeature);
          const areaFeature = createMeasurementLabelFeature(
            center,
            `Area ${formatAreaSquareMeters(draftShapeMetrics.areaSqm)}`,
            'draft-shape-area',
            'area',
          );
          if (areaFeature) {
            features.push(areaFeature);
          }
        }
      } else if (shapeType === DRAW_MODE.CIRCLE) {
        const center = getFeatureCenter(styledDraftFeature);
        if (center && Number.isFinite(Number(draftShapeMetrics.areaSqm))) {
          const areaFeature = createMeasurementLabelFeature(
            center,
            `Area ${formatAreaSquareMeters(draftShapeMetrics.areaSqm)}`,
            'draft-circle-area',
            'area',
          );
          if (areaFeature) {
            features.push(areaFeature);
          }
        }
        if (center && Number.isFinite(Number(draftShapeMetrics.radiusMeters)) && Number(draftShapeMetrics.radiusMeters) > 0) {
          const radiusEdge = destinationPoint(center, 90, Number(draftShapeMetrics.radiusMeters));
          const radiusMidPoint = getMidPointLatLng(center, radiusEdge);
          const radiusFeature = createMeasurementLabelFeature(
            radiusMidPoint,
            `R ${formatDistanceMeters(draftShapeMetrics.radiusMeters)}`,
            'draft-circle-radius',
            'radius',
          );
          if (radiusFeature) {
            features.push(radiusFeature);
          }
        }
      }
    }

    if (!features.length) return null;

    return {
      type: 'FeatureCollection',
      features,
    };
  }, [
    drawMode,
    liveShapeMetrics,
    polygonLivePoints,
    previewRectangle,
    circleCenter,
    styledDraftFeature,
    draftShapeMetrics,
    draftShapeType,
  ]);
  const measurementLayerKey = useMemo(() => {
    if (!measurementLabelGeoJson || !Array.isArray(measurementLabelGeoJson.features)) {
      return 'geo-fence-live-measurement-layer-empty';
    }
    const signature = measurementLabelGeoJson.features
      .map((feature) => {
        const coords = feature && feature.geometry && feature.geometry.coordinates;
        const text = feature && feature.properties && feature.properties.text;
        return `${feature.id || ''}:${Array.isArray(coords) ? `${coords[0]}:${coords[1]}` : ''}:${text || ''}`;
      })
      .join('|');
    return `geo-fence-live-measurement-layer-${signature}`;
  }, [measurementLabelGeoJson]);
  const savedFenceLayerKey = useMemo(() => {
    const latestUpdatedAt = (geoFences || []).reduce((latest, item) => {
      const ts = Number(new Date(item && item.updatedAt)) || 0;
      return Math.max(latest, ts);
    }, 0);
    return `saved-fence-layer-${(geoFences || []).length}-${latestUpdatedAt}-${shouldBindSavedFenceTooltips ? 'tooltip' : 'plain'}`;
  }, [geoFences, shouldBindSavedFenceTooltips]);
  const pendingFenceLayerKey = useMemo(() => {
    const signature = (pendingFences || []).map((item) => String(item && item.localId)).join('|');
    return `pending-fence-layer-${signature}`;
  }, [pendingFences]);
  const isDrawingInProgress = drawMode !== DRAW_MODE.NONE
    || workingPoints.length > 0
    || !!rectangleStart
    || !!circleCenter;
  const editingExistingFenceId = editingExistingFence && editingExistingFence._id;

  useEffect(() => {
    if (drawMode !== DRAW_MODE.POLYGON) return undefined;

    const handlePolygonKeyboardFinish = (event) => {
      if (!event || event.key !== 'Enter') return;

      const target = event.target || {};
      const tagName = target.tagName ? String(target.tagName).toLowerCase() : '';
      const isTypingField = target.isContentEditable
        || tagName === 'input'
        || tagName === 'textarea'
        || tagName === 'select';
      if (isTypingField) return;
      if (workingPoints.length < 3) return;

      event.preventDefault();
      finishPolygon();
    };

    window.addEventListener('keydown', handlePolygonKeyboardFinish);
    return () => {
      window.removeEventListener('keydown', handlePolygonKeyboardFinish);
    };
  }, [drawMode, workingPoints]);

  if (!hasPointOfInterestAccess) {
    return (
      <Card>
        <Alert
          type="error"
          showIcon
          message="Unauthorized"
          description="You do not have Point of Interest access."
        />
      </Card>
    );
  }

  return (
    <div className="geo-fence-page">
      <Row gutter={12}>
        <Col xs={24} md={8} lg={7} xl={6}>
          <Card title="Point of Interest Manager" className="geo-fence-panel">
            <div className="geo-fence-field">
              <label>Search Location</label>
              <div className="geo-fence-search-row">
                <Input
                  value={mapSearchQuery}
                  onChange={(event) => setMapSearchQuery(event.target.value)}
                  onPressEnter={handleMapSearch}
                  placeholder="Place or lat,lng (e.g. 23.0225,72.5714)"
                />
                <Button type="primary" onClick={handleMapSearch} loading={searchingLocation}>
                  Go
                </Button>
              </div>
            </div>

            {isSuperAdmin && (
              <div className="geo-fence-field">
                <label>Organisation(s)</label>
                <Select
                  mode="multiple"
                  value={selectedOrganisations}
                  showSearch
                  optionFilterProp="children"
                  onChange={(values) => setSelectedOrganisations(values || [])}
                  placeholder="Select one or more organisations"
                  maxTagCount={2}
                  style={{ width: '100%' }}
                >
                  {organisations.map((org) => (
                    <Option key={org._id} value={String(org._id)}>
                      {org.name}
                    </Option>
                  ))}
                </Select>
                <small className="geo-fence-help-text">
                  Saved point of interest will be created for all selected organisations.
                </small>
              </div>
            )}

            <div className="geo-fence-field">
              <label>Draw Shape</label>
              <div className="geo-fence-btn-row">
                <Button
                  type={drawMode === DRAW_MODE.POLYGON ? 'primary' : 'default'}
                  onClick={() => setMode(DRAW_MODE.POLYGON)}
                  disabled={!canDrawPointOfInterest}
                >
                  Polygon
                </Button>
                <Button
                  type={drawMode === DRAW_MODE.RECTANGLE ? 'primary' : 'default'}
                  onClick={() => setMode(DRAW_MODE.RECTANGLE)}
                  disabled={!canDrawPointOfInterest}
                >
                  Rectangle
                </Button>
                <Button
                  type={drawMode === DRAW_MODE.CIRCLE ? 'primary' : 'default'}
                  onClick={() => setMode(DRAW_MODE.CIRCLE)}
                  disabled={!canDrawPointOfInterest}
                >
                  Circle
                </Button>
                <Button onClick={cancelActiveDrawing} disabled={!isDrawingInProgress}>Stop Draw</Button>
              </div>
            </div>

            {drawMode === DRAW_MODE.POLYGON && (
              <div className="geo-fence-field">
                <Tag color="blue">Click map to add points, press Enter to finish</Tag>
                <div className="geo-fence-btn-row">
                  <Button onClick={finishPolygon} disabled={workingPoints.length < 3 || !canDrawPointOfInterest}>Finish Polygon (Enter)</Button>
                  <Button onClick={() => setWorkingPoints((prev) => prev.slice(0, -1))} disabled={!workingPoints.length || !canDrawPointOfInterest}>Undo Point</Button>
                </div>
              </div>
            )}

            {drawMode === DRAW_MODE.RECTANGLE && (
              <div className="geo-fence-field">
                <Tag color="blue">{rectangleStart ? 'Click second point to finish rectangle' : 'Click first corner to start rectangle'}</Tag>
              </div>
            )}

            {drawMode === DRAW_MODE.CIRCLE && (
              <div className="geo-fence-field">
                <Tag color="blue">{circleCenter ? 'Click second point to finish circle' : 'Click center point to start circle'}</Tag>
              </div>
            )}

            <Divider />

            <div className="geo-fence-field">
              <label>Point of Interest Name</label>
              <Input
                value={shapeName}
                onChange={(event) => setShapeName(event.target.value)}
                placeholder="e.g. Service Yard Zone A"
                disabled={!canDrawPointOfInterest}
              />
            </div>

            <Row gutter={8}>
              <Col span={10}>
                <div className="geo-fence-field">
                  <label>Color</label>
                  <Input
                    type="color"
                    value={shapeColor}
                    onChange={(event) => setShapeColor(event.target.value)}
                    disabled={!canDrawPointOfInterest}
                  />
                </div>
              </Col>
              <Col span={14}>
                <div className="geo-fence-field">
                  <label>Stroke Width</label>
                  <InputNumber
                    min={1}
                    max={10}
                    value={strokeWeight}
                    onChange={(value) => setStrokeWeight(value || 1)}
                    style={{ width: '100%' }}
                    disabled={!canDrawPointOfInterest}
                  />
                </div>
              </Col>
            </Row>

            <div className="geo-fence-field">
              <label>Fill Opacity</label>
              <Slider
                min={0}
                max={1}
                step={0.05}
                value={fillOpacity}
                onChange={(value) => setFillOpacity(value)}
                disabled={!canDrawPointOfInterest}
              />
            </div>

            <div className="geo-fence-field">
              <label>{drawMode !== DRAW_MODE.NONE ? 'Live Measurements' : 'Shape Measurements'}</label>
              <div className="geo-fence-metrics-box">
                {activeMetricLines.length ? activeMetricLines.map((line, index) => (
                  <div key={`metric-${index}`} className="geo-fence-metric-line">{line}</div>
                )) : (
                  <div className="geo-fence-metrics-empty">
                    {drawMode !== DRAW_MODE.NONE
                      ? 'Add points on map to see measurements.'
                      : 'Draw or select a shape to view measurements.'}
                  </div>
                )}
              </div>
            </div>

            {editingExistingFence && (
              <div className="geo-fence-field">
                <Tag color="orange">Editing existing point of interest: {editingExistingFence.name}</Tag>
              </div>
            )}

            <div className="geo-fence-btn-row">
              {!editingExistingFence && canCreatePointOfInterest && (
                <Button type="primary" onClick={addCurrentShapeToBatch} disabled={!draftFeature}>
                  Add To Batch
                </Button>
              )}
              {editingExistingFence && canUpdatePointOfInterest && (
                <Button type="primary" onClick={updateExistingFence} loading={savingFence} disabled={!draftFeature}>
                  Update Point of Interest
                </Button>
              )}
              <Button
                onClick={clearCurrentDraftOnly}
                disabled={(!draftFeature && !shapeName) || (!canDrawPointOfInterest && !editingExistingFence)}
              >
                {editingExistingFence ? 'Cancel Edit' : 'Clear Draft'}
              </Button>
              {editingExistingFence && canDeletePointOfInterest && (
                <Popconfirm
                  title="Delete this point of interest?"
                  onConfirm={() => handleDelete(editingExistingFence._id)}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button type="danger" disabled={savingFence}>Delete Point of Interest</Button>
                </Popconfirm>
              )}
            </div>

            {canCreatePointOfInterest && (
              <div className="geo-fence-btn-row geo-fence-batch-actions">
                <Button type="primary" onClick={saveAllPendingShapes} loading={savingAllPending} disabled={!pendingFences.length}>
                  Save All Pending ({pendingFences.length})
                </Button>
                <Popconfirm
                  title="Remove all pending shapes?"
                  onConfirm={clearAllPendingFences}
                  okText="Yes"
                  cancelText="No"
                  disabled={!pendingFences.length}
                >
                  <Button disabled={!pendingFences.length}>Clear Pending</Button>
                </Popconfirm>
              </div>
            )}

            <Divider />

            {canCreatePointOfInterest && (
              <div className="geo-fence-list-wrap">
                <h4>Pending Shapes ({pendingFences.length})</h4>
                <List
                  size="small"
                  dataSource={pendingFences}
                  locale={{ emptyText: 'No pending shapes' }}
                  renderItem={(item) => {
                    const resolvedShapeType = resolveShapeType(item && item.feature, item && item.shapeType);
                    const metrics = computeShapeMetrics(item && item.feature, resolvedShapeType);
                    const primaryMetricSummary = getMetricPrimarySummary(metrics, resolvedShapeType);
                    const segmentMetricSummary = getMetricSegmentSummary(metrics, resolvedShapeType);
                    return (
                      <List.Item
                        className={`geo-fence-list-item${selectedPendingFenceId === item.localId ? ' geo-fence-list-item-selected' : ''}`}
                        onClick={() => setSelectedPendingFenceId(item.localId)}
                        actions={[
                          <Button
                            key={`edit-pending-${item.localId}`}
                            size="small"
                            onClick={(event) => {
                              event.stopPropagation();
                              startEditPendingFence(item.localId);
                            }}
                          >
                            Edit
                          </Button>,
                          <Popconfirm
                            key={`delete-pending-${item.localId}`}
                            title="Remove this pending shape?"
                            onConfirm={() => removePendingFence(item.localId)}
                            okText="Yes"
                            cancelText="No"
                          >
                            <Button
                              size="small"
                              type="danger"
                              onClick={(event) => event.stopPropagation()}
                            >
                              Remove
                            </Button>
                          </Popconfirm>,
                        ]}
                      >
                        <List.Item.Meta
                          title={item.name}
                          description={(
                            <div className="geo-fence-shape-meta">
                              <div>{resolvedShapeType}</div>
                              {primaryMetricSummary ? <div className="geo-fence-shape-meta-secondary">{primaryMetricSummary}</div> : null}
                              {segmentMetricSummary ? <div className="geo-fence-shape-meta-secondary">{segmentMetricSummary}</div> : null}
                            </div>
                          )}
                        />
                      </List.Item>
                    );
                  }}
                />
              </div>
            )}

            {canCreatePointOfInterest && selectedPendingFence && (
              <div className="geo-fence-field">
                <Tag color="gold">Selected pending: {selectedPendingFence.name}</Tag>
                <div className="geo-fence-btn-row">
                  <Button onClick={() => startEditPendingFence(selectedPendingFence.localId)}>Edit Selected</Button>
                  <Popconfirm
                    title="Remove selected pending shape?"
                    onConfirm={() => removePendingFence(selectedPendingFence.localId)}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button type="danger">Remove Selected</Button>
                  </Popconfirm>
                </div>
              </div>
            )}

            <Divider />

            <div className="geo-fence-list-wrap">
              <h4>Saved Points of Interest</h4>
              <div className="geo-fence-field">
                <Input
                  value={savedFenceSearchQuery}
                  onChange={(event) => setSavedFenceSearchQuery(event.target.value)}
                  placeholder="Search saved points by name"
                />
              </div>
              {canDeletePointOfInterest && (
                <div className="geo-fence-btn-row">
                  <Checkbox
                    checked={!!(filteredSavedGeoFences.length && filteredSavedGeoFences.every((item) => selectedExistingFenceIds.includes(String(item._id))))}
                    indeterminate={!!(filteredSavedGeoFences.length
                      && filteredSavedGeoFences.some((item) => selectedExistingFenceIds.includes(String(item._id)))
                      && !filteredSavedGeoFences.every((item) => selectedExistingFenceIds.includes(String(item._id))))}
                    onChange={(event) => toggleSelectAllFilteredSavedFences(!!(event && event.target && event.target.checked))}
                  >
                    Select filtered ({filteredSavedGeoFences.length})
                  </Checkbox>
                  <Popconfirm
                    title={`Delete ${selectedExistingFenceIds.length} selected point(s) of interest?`}
                    onConfirm={deleteSelectedSavedFences}
                    okText="Yes"
                    cancelText="No"
                    disabled={!selectedExistingFenceIds.length}
                  >
                    <Button type="danger" disabled={!selectedExistingFenceIds.length}>Delete Selected ({selectedExistingFenceIds.length})</Button>
                  </Popconfirm>
                </div>
              )}
              {canReadPointOfInterest ? (
                <List
                  loading={loadingFences}
                  size="small"
                  dataSource={filteredSavedGeoFences}
                  locale={{ emptyText: isSuperAdmin ? 'No points of interest for selected organisation(s)' : 'No points of interest for your organisation' }}
                  renderItem={(item) => {
                    const resolvedShapeType = resolveShapeType(item && item.feature, item && item.shapeType);
                    const metrics = computeShapeMetrics(item && item.feature, resolvedShapeType);
                    const primaryMetricSummary = getMetricPrimarySummary(metrics, resolvedShapeType);
                    const segmentMetricSummary = getMetricSegmentSummary(metrics, resolvedShapeType);
                    const rowActions = [];
                    if (canUpdatePointOfInterest) {
                      rowActions.push(
                        <Button
                          key={`edit-${item._id}`}
                          size="small"
                          onClick={(event) => {
                            event.stopPropagation();
                            startEditExistingFence(item);
                          }}
                        >
                          Edit
                        </Button>
                      );
                    }
                    if (canDeletePointOfInterest) {
                      rowActions.push(
                        <Popconfirm
                          key={`delete-${item._id}`}
                          title="Delete this point of interest?"
                          onConfirm={() => handleDelete(item._id)}
                          okText="Yes"
                          cancelText="No"
                        >
                          <Button
                            size="small"
                            type="danger"
                            onClick={(event) => event.stopPropagation()}
                          >
                            Delete
                          </Button>
                        </Popconfirm>
                      );
                    }

                    return (
                      <List.Item
                        className={`geo-fence-list-item${(selectedExistingFenceId === item._id || editingExistingFenceId === item._id) ? ' geo-fence-list-item-selected' : ''}`}
                        onClick={() => {
                          if (canEditExistingPointOfInterest) {
                            startEditExistingFence(item);
                            return;
                          }
                          setSelectedExistingFenceId(item && item._id ? item._id : null);
                        }}
                        actions={rowActions}
                      >
                        <List.Item.Meta
                          title={(
                            <div className="geo-fence-btn-row">
                              {canDeletePointOfInterest ? (
                                <Checkbox
                                  checked={selectedExistingFenceIds.includes(String(item._id))}
                                  onChange={(event) => {
                                    event.stopPropagation();
                                    toggleExistingFenceSelection(item && item._id, !!(event && event.target && event.target.checked));
                                  }}
                                  onClick={(event) => event.stopPropagation()}
                                />
                              ) : null}
                              <span>{item.name}</span>
                            </div>
                          )}
                          description={(
                            <div className="geo-fence-shape-meta">
                              <div>{`${resolvedShapeType}${item._sourceOrganisationName ? ` | ${item._sourceOrganisationName}` : ''}`}</div>
                              {primaryMetricSummary ? <div className="geo-fence-shape-meta-secondary">{primaryMetricSummary}</div> : null}
                              {segmentMetricSummary ? <div className="geo-fence-shape-meta-secondary">{segmentMetricSummary}</div> : null}
                            </div>
                          )}
                        />
                      </List.Item>
                    );
                  }}
                />
              ) : (
                <small className="geo-fence-help-text">Read access is required to view saved points of interest.</small>
              )}
            </div>
          </Card>
        </Col>
        <Col xs={24} md={16} lg={17} xl={18}>
          <Card className="geo-fence-map-card">
            <div className="geo-fence-map-wrap">
              <BaseMap
                mapRef={mapRef}
                center={mapCenter}
                zoom={mapZoom}
                onZoomEnd={handleMapZoomEnd}
                onMoveend={handleMapMoveEnd}
                onClick={handleMapClick}
                onMousemove={handleMapMouseMove}
                doubleClickZoom={drawMode !== DRAW_MODE.POLYGON}
                attributionControl={false}
                preferCanvas
                showIndiaGeoJson={false}
                showMaaGeoJson={false}
                showGeoFenceOverlay={false}
                geoJsonLabelsMinZoom={20}
                style={{ height: '100%', width: '100%' }}
              >
                {savedFenceFeatureCollection && (
                  <GeoJSON
                    key={savedFenceLayerKey}
                    data={savedFenceFeatureCollection}
                    style={(feature) => {
                      const fenceId = feature && feature.properties && feature.properties._fenceId;
                      const fence = savedFenceById[String(fenceId)] || {};
                      return getInteractiveLayerStyle(feature, fence, {
                        selected: String(selectedExistingFenceId || '') === String(fenceId || '')
                          || String(editingExistingFenceId || '') === String(fenceId || ''),
                      });
                    }}
                    onEachFeature={(feature, layer) => {
                      const fenceId = feature && feature.properties && feature.properties._fenceId;
                      const fence = savedFenceById[String(fenceId)];
                      if (shouldBindSavedFenceTooltips && fence) {
                        const label = (feature && feature.properties && feature.properties.name) || fence.name;
                        const shapeType = resolveShapeType(feature, fence && fence.shapeType);
                        const metrics = computeShapeMetrics(feature, shapeType);
                        if (label || metrics) {
                          layer.bindTooltip(buildShapeTooltipHtml(label, metrics, shapeType), { sticky: true, direction: 'top' });
                        }
                      }
                      layer.on('click', (event) => handleExistingFenceLayerClick(fence, event));
                    }}
                  />
                )}

                {pendingFenceFeatureCollection && (
                  <GeoJSON
                    key={pendingFenceLayerKey}
                    data={pendingFenceFeatureCollection}
                    style={(feature) => {
                      const fenceId = feature && feature.properties && feature.properties._fenceId;
                      const fence = pendingFenceById[String(fenceId)] || {};
                      return getInteractiveLayerStyle(feature, fence, {
                        pending: true,
                        selected: String(selectedPendingFenceId || '') === String(fenceId || ''),
                      });
                    }}
                    onEachFeature={(feature, layer) => {
                      const fenceId = feature && feature.properties && feature.properties._fenceId;
                      const fence = pendingFenceById[String(fenceId)];
                      if (!fence) return;
                      const label = (feature && feature.properties && feature.properties.name) || fence.name;
                      const shapeType = resolveShapeType(feature, fence && fence.shapeType);
                      const metrics = computeShapeMetrics(feature, shapeType);
                      if (label || metrics) {
                        layer.bindTooltip(buildShapeTooltipHtml(label, metrics, shapeType), { sticky: true, direction: 'top' });
                      }
                      layer.on('click', (event) => handlePendingFenceLayerClick(fence, event));
                    }}
                  />
                )}

                {drawMode === DRAW_MODE.POLYGON && workingPoints.length > 0 && (
                  <Polyline
                    positions={previewPoint ? workingPoints.concat([previewPoint]) : workingPoints}
                    color={shapeColor}
                    weight={strokeWeight}
                  />
                )}

                {drawMode === DRAW_MODE.RECTANGLE && previewRectangle.length > 0 && (
                  <Polygon
                    positions={previewRectangle}
                    color={shapeColor}
                    fillColor={shapeColor}
                    fillOpacity={fillOpacity}
                    weight={strokeWeight}
                  />
                )}

                {drawMode === DRAW_MODE.CIRCLE && circleCenter && circleRadiusMeters > 0 && (
                  <Circle
                    center={circleCenter}
                    radius={circleRadiusMeters}
                    color={shapeColor}
                    fillColor={shapeColor}
                    fillOpacity={fillOpacity}
                    weight={strokeWeight}
                  />
                )}

                {draftFeature && (
                  <GeoJSON
                    data={styledDraftFeature}
                    style={(feature) => getInteractiveLayerStyle(feature, {}, { selected: true })}
                    onEachFeature={(feature, layer) => {
                      const label = shapeName || 'Draft Point of Interest';
                      const shapeType = resolveShapeType(feature, draftShapeType);
                      const metrics = computeShapeMetrics(feature, shapeType);
                      if (label || metrics) {
                        layer.bindTooltip(buildShapeTooltipHtml(label, metrics, shapeType), { sticky: true, direction: 'top' });
                      }
                      layer.on('click', (event) => stopLeafletEventPropagation(event));
                    }}
                  />
                )}

                {measurementLabelGeoJson && measurementLabelGeoJson.features && measurementLabelGeoJson.features.length > 0 && (
                  <GeoJSON
                    key={measurementLayerKey}
                    data={measurementLabelGeoJson}
                    interactive={false}
                    pointToLayer={(feature, latlng) => {
                      const labelText = feature && feature.properties && feature.properties.text;
                      const labelKind = feature && feature.properties && feature.properties.kind;
                      return L.marker(latlng, {
                        icon: buildMeasurementLabelIcon(labelText, labelKind),
                        interactive: false,
                        keyboard: false,
                      });
                    }}
                  />
                )}
              </BaseMap>
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
